/**
 * Upload Queue React Hook
 * 
 * Provides reactive access to the upload queue state
 */

import { useState, useEffect, useCallback } from 'react';
import { uploadQueue, QueueState, QueuedUpload } from '@/lib/uploadQueue';

export function useUploadQueue() {
  const [state, setState] = useState<QueueState>(() => uploadQueue.getState());

  useEffect(() => {
    return uploadQueue.subscribe(setState);
  }, []);

  const enqueue = useCallback(async (
    questionId: string,
    testResponseId: string,
    videoBlob?: Blob,
    audioBlob?: Blob,
    videoMimeType?: string,
    audioMimeType?: string
  ): Promise<string> => {
    return uploadQueue.enqueue(
      questionId,
      testResponseId,
      videoBlob,
      audioBlob,
      videoMimeType,
      audioMimeType
    );
  }, []);

  const retry = useCallback((id: string) => {
    uploadQueue.retry(id);
  }, []);

  const markAsSaved = useCallback(async (id: string) => {
    await uploadQueue.markAsSaved(id);
  }, []);

  const getUploadByQuestionId = useCallback((questionId: string): QueuedUpload | undefined => {
    return uploadQueue.getUploadByQuestionId(questionId);
  }, []);

  const pause = useCallback(() => {
    uploadQueue.pause();
  }, []);

  const resume = useCallback(() => {
    uploadQueue.resume();
  }, []);

  return {
    // State
    uploads: state.uploads,
    activeCount: state.activeCount,
    queuedCount: state.queuedCount,
    isOnline: state.isOnline,
    isPaused: state.isPaused,
    circuitOpen: state.circuitOpen,
    
    // Computed
    isUploading: state.activeCount > 0,
    hasPendingUploads: state.activeCount > 0 || state.queuedCount > 0,
    pendingCount: state.activeCount + state.queuedCount,
    
    // Actions
    enqueue,
    retry,
    markAsSaved,
    getUploadByQuestionId,
    pause,
    resume,
  };
}
